/**
 * Calculate heading based on two GPS positions in degrees
 *
 * On a flat plane, this function would be simpler (using basic trigonometry), but we need to
 * account for the curvature of the Earth.
 *
 * For reference on the formula, see:
 * - https://stackoverflow.com/a/18738281/30583479
 * - https://www.movable-type.co.uk/scripts/latlong.html
 *
 * @param prevPosition Previous position of the aircraft
 * @param currentPosition Current position of the aircraft
 * @returns Estimated heading in degrees (0-359)
 */
export const getEstimatedHeading = (
  prevPosition: { latitude: number; longitude: number } | undefined,
  currentPosition: { latitude: number; longitude: number } | undefined
): number => {
  if (!prevPosition || !currentPosition) {
    return 0;
  }

  // Convert from scaled degrees (degrees * 1e7) to radians
  // We need radians because trigonometric functions (sin/cos) expect radians
  const TO_RADIAN = Math.PI / 180 / 1e7;
  const lat1 = prevPosition.latitude * TO_RADIAN;
  const lon1 = prevPosition.longitude * TO_RADIAN;
  const lat2 = currentPosition.latitude * TO_RADIAN;
  const lon2 = currentPosition.longitude * TO_RADIAN;

  // Skip calculation if positions are identical or very close
  if (Math.abs(lat2 - lat1) < 1e-9 && Math.abs(lon2 - lon1) < 1e-9) {
    return 0;
  }

  // Calculate spherical heading components in radians
  // y component (east-west movement):
  // - Math.sin(lon2 - lon1): Calculates east-west difference
  // - Math.cos(lat2): Adjusts for longitude convergence at poles
  const y = Math.sin(lon2 - lon1) * Math.cos(lat2);

  // x component (north-south movement):
  // - First term (Math.cos(lat1) * Math.sin(lat2)): North-south difference
  // - Second term (Math.sin(lat1) * Math.cos(lat2) * Math.cos(lon2 - lon1)): Adjusts for Earth's curvature
  const x =
    Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(lon2 - lon1);

  // Convert the spherical heading back to degrees
  // atan2 returns -PI to PI, so we normalize to 0-359
  const heading = ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;

  return heading;
};
